import { useEffect } from 'react';
import type { Job } from '../types';

interface RunConfigModalProps {
  job: Job | null;
  onClose: () => void;
}

export function RunConfigModal({ job, onClose }: RunConfigModalProps) {
  useEffect(() => {
    if (!job) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [job, onClose]);

  if (!job) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-primary-900/40 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-2xl shadow-card-hover border border-primary-100 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-primary-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-primary-400">Config used</div>
            <div className="text-sm font-mono text-primary-900 truncate">{job.scan_id}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-primary-400 hover:text-primary-700 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-3">
          {job.config_used ? (
            <pre className="text-xs font-mono bg-primary-50 border border-primary-100 rounded-lg p-3 max-h-[60vh] overflow-auto whitespace-pre-wrap break-all">
              {JSON.stringify(job.config_used, null, 2)}
            </pre>
          ) : (
            <div className="text-sm text-primary-500">
              No config snapshot stored for this job.
            </div>
          )}
          <p className="text-[11px] text-primary-400">
            This is a read-only snapshot of the video-generation parameters that were active at the
            moment this job started. Editing them now in Settings affects future jobs only.
          </p>
        </div>
      </div>
    </div>
  );
}
